import {
  CalendarOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EditOutlined,
  FileOutlined,
  FolderOutlined,
  PlusOutlined,
  SettingOutlined,
  SwapOutlined,
  UserOutlined
} from "@ant-design/icons"
import {
  Empty,
  Flex,
  Skeleton,
  Spin,
  Table,
  type TableProps,
  Tag,
  Typography
} from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useLoadOnScroll } from "../../../../hooks/use-load-on-scroll.ts"
import {
  type OverviewAccent,
  OverviewCardIcon
} from "../overview/OverviewCardIcon.tsx"
import type {
  LeadActivityCategory,
  LeadActivityEntry
} from "./lead-activity.types.ts"
import { useLeadActivityFormat } from "./use-lead-activity-format.ts"

const { Text } = Typography

const BATCH_SIZE = 20
const REVEAL_DELAY_MS = 200

const categoryColor: Record<LeadActivityCategory, string> = {
  appointments: "blue",
  followUps: "orange",
  notes: "purple",
  stageChanges: "green",
  system: "gray"
}

type IconSpec = { icon: React.ReactNode; accent: OverviewAccent }

const categoryIcon: Record<LeadActivityCategory, IconSpec> = {
  appointments: { icon: <CalendarOutlined />, accent: "blue" },
  followUps: { icon: <ClockCircleOutlined />, accent: "orange" },
  notes: { icon: <EditOutlined />, accent: "indigo" },
  stageChanges: { icon: <SwapOutlined />, accent: "teal" },
  system: { icon: <SettingOutlined />, accent: "teal" }
}

/**
 * `entry.description` is built by `describeChanges()` in lead-activity.ts as
 * `"<Humanized Field Name>: <from> → <to>"` off the backend's own change
 * keys — never localized, so matching its English prefix is stable
 * regardless of UI language. Several real change keys (`attachment`,
 * `checklist`) share a broader `category` bucket (`appointments`) for
 * filtering purposes, which the user-facing icon can still tell apart by
 * reading the same text already on screen — this only changes which icon a
 * row gets, never the category used to filter or badge it.
 */
const DESCRIPTION_ICON_RULES: Array<{ test: RegExp; spec: IconSpec }> = [
  {
    test: /^(Sales Price|Budget|Estimate|Margin|Payable|Receivable|Planned)/i,
    spec: { icon: <DollarOutlined />, accent: "green" }
  },
  {
    test: /^(Customer|Email|Phone|Contact)/i,
    spec: { icon: <UserOutlined />, accent: "blue" }
  },
  {
    test: /^(Attachment|File)/i,
    spec: { icon: <FileOutlined />, accent: "orange" }
  },
  {
    test: /^Checklist/i,
    spec: { icon: <CheckSquareOutlined />, accent: "teal" }
  },
  {
    test: /^(Project Type|Scope|Description|Timeline|Start Date|End Date|Budget Range)/i,
    spec: { icon: <FolderOutlined />, accent: "indigo" }
  }
]

const isCreateEntry = (entry: LeadActivityEntry) =>
  entry.titleKey.endsWith(".leadCreated") || entry.titleKey.endsWith(".created")

/**
 * A small pastel icon per row, reusing Overview's own `OverviewCardIcon`.
 * Only 5 real categories exist for filtering, but a description-text rule
 * picks a more specific icon within them first — otherwise every "system"
 * row (budget, milestones, files, checklist…) would show the same gear.
 */
const iconForEntry = (entry: LeadActivityEntry): IconSpec => {
  if (isCreateEntry(entry)) return { icon: <PlusOutlined />, accent: "green" }

  const byDescription = entry.description
    ? DESCRIPTION_ICON_RULES.find((rule) => rule.test.test(entry.description!))
    : undefined

  return byDescription?.spec ?? categoryIcon[entry.category]
}

interface LeadActivityTimelineProps {
  entries: LeadActivityEntry[]
  loading?: boolean
  filtered?: boolean
}

export function LeadActivityTimeline({
  entries,
  loading,
  filtered
}: LeadActivityTimelineProps) {
  const { t } = useTranslation()
  const { formatMoment } = useLeadActivityFormat()
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)
  const [revealing, setRevealing] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const revealingRef = useRef(false)

  useEffect(() => setVisibleCount(BATCH_SIZE), [entries])

  const hasMore = visibleCount < entries.length

  const revealMore = useCallback(() => {
    if (revealingRef.current || !hasMore) return
    revealingRef.current = true
    setRevealing(true)
    window.setTimeout(() => {
      setVisibleCount((count) => Math.min(count + BATCH_SIZE, entries.length))
      setRevealing(false)
      revealingRef.current = false
    }, REVEAL_DELAY_MS)
  }, [hasMore, entries.length])

  useLoadOnScroll({
    dependency: entries,
    childrenRef: sentinelRef,
    observerCallback: revealMore,
    observer: observerRef
  })

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />

  if (!entries.length)
    return (
      <Flex justify={"center"} className={"py-8"}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t(
            filtered
              ? "leadWorkspace.activity.timeline.noMatches"
              : "leadWorkspace.activity.timeline.empty"
          )}
        />
      </Flex>
    )

  const shown = entries.slice(0, visibleCount)

  const columns: TableProps<LeadActivityEntry>["columns"] = [
    {
      key: "activity",
      title: t("leadWorkspace.activity.timeline.columns.activity"),
      width: "32%",
      render: (_, entry) => {
        const { icon, accent } = iconForEntry(entry)
        return (
          <Flex gap={"small"} align={"center"}>
            {/* A muted node on the row's own leading edge — the last trace
                of the timeline concept once each entry became a table row,
                rather than antd's own connected-line Timeline component,
                which has no per-row column to sit inside. */}
            <span
              aria-hidden
              className={"h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300"}
            />
            <OverviewCardIcon icon={icon} accent={accent} size={26} />
            <Flex wrap gap={6} align={"center"} className={"min-w-0"}>
              <Text strong className={"text-sm"}>
                {t(entry.titleKey)}
              </Text>
              <Tag color={categoryColor[entry.category]} className={"m-0"}>
                {t(`leadWorkspace.activity.categories.${entry.category}`)}
              </Tag>
            </Flex>
          </Flex>
        )
      }
    },
    {
      key: "details",
      title: t("leadWorkspace.activity.timeline.columns.details"),
      render: (_, entry) =>
        entry.description ? (
          <Text className={"text-sm text-gray-600"}>{entry.description}</Text>
        ) : (
          <Text type={"secondary"}>—</Text>
        )
    },
    {
      key: "at",
      title: t("leadWorkspace.activity.timeline.columns.at"),
      width: 160,
      render: (_, entry) => (
        <Text type={"secondary"} className={"text-xs"}>
          {formatMoment(entry.at)}
        </Text>
      )
    },
    {
      key: "user",
      title: t("leadWorkspace.activity.timeline.columns.user"),
      width: 140,
      render: (_, entry) => (
        <Text type={"secondary"} className={"text-xs"}>
          {entry.userLabel || t("leadWorkspace.activity.timeline.system")}
        </Text>
      )
    }
  ]

  return (
    <Flex
      vertical
      gap={"small"}
      className={"w-full"}
      data-testid={"lead-activity-timeline"}
    >
      <Table<LeadActivityEntry>
        size={"middle"}
        rowKey={"id"}
        pagination={false}
        columns={columns}
        dataSource={shown}
        onRow={
          ((entry: LeadActivityEntry) => ({
            "data-testid": `activity-entry-${entry.id}`
          })) as TableProps<LeadActivityEntry>["onRow"]
        }
      />

      {hasMore && <div ref={sentinelRef} className={"h-px"} />}

      {revealing && (
        <Flex
          vertical
          align={"center"}
          gap={2}
          className={"rounded-card bg-fill-quaternary py-3"}
        >
          <Spin size={"small"} />
          <Text type={"secondary"} className={"text-xs"}>
            {t("leadWorkspace.activity.timeline.loadingMore")}
          </Text>
          <Text type={"secondary"} className={"text-xs"}>
            {t("leadWorkspace.activity.timeline.newestFirst")}
          </Text>
        </Flex>
      )}

      {!hasMore && shown.length > 1 && (
        <Text type={"secondary"} className={"text-center text-xs"}>
          {t("leadWorkspace.activity.timeline.allLoaded")}
        </Text>
      )}
    </Flex>
  )
}
